import React from 'react';
import { formatColor } from '@meitu/saas-colors';
import {
    type IFontBackground,
    type IFontGlow,
    type IFontShadow,
    type IFontStroke,
    type IFontTextGradient,
} from '@meitu/saas-editor-core/es/version-types';
import './style.less';

const DEFAULT_COLOR = '#000000';

const getGlowStyle = (glow: IFontGlow, offset: number) => {
    const { width, color } = glow;
    const size = Math.max(width, offset);

    const style: React.CSSProperties = {
        color,
        WebkitTextStroke: `${size}px ${color}`,
        filter: `blur(${Math.ceil(size / 2)}px)`,
    };

    return style;
};

const getShadowStyle = (shadow: IFontShadow) => {
    const { color, x, y, blur } = shadow;

    const style: React.CSSProperties = {
        textShadow: `${x}px ${y}px ${blur}px ${color}`,
    };

    return style;
};

const getStrokeStyle = (strokes: IFontStroke[], stroke: IFontStroke, index: number) => {
    const { color } = stroke;
    const sliceEnd = Math.min(index + 1, strokes?.length);
    const strokeWidth = strokes.slice(0, sliceEnd).reduce((sum, stroke) => {
        const width = Number(stroke.width);
        return sum + (Number.isFinite(width) ? width : 0);
    }, 0);

    const style: React.CSSProperties = {
        WebkitTextStroke: `${strokeWidth * 2}px ${color}`,
    };

    return style;
};

const getGradientStyle = (textGradient: IFontTextGradient) => {
    const backgroundImage = formatColor(textGradient) as string;

    const styles: React.CSSProperties = {
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundImage,
    };

    return styles;
};

const getColorStyle = (color: string, textGradient?: IFontTextGradient) => {
    if (!textGradient || textGradient.colors.length === 0) return { color };
    return getGradientStyle(textGradient);
};

type Props = {
    usage?: 'entry' | 'thumbnail';
    color: string;
    textStroke: IFontStroke[];
    textShadow: IFontShadow[];
    textGlow: IFontGlow[];
    textGradient?: IFontTextGradient;
    textBackground?: IFontBackground[];
};

export const TextEffectDisplay: React.FC<Props> = ({
    usage = 'thumbnail',
    color,
    textStroke = [],
    textShadow = [],
    textGlow = [],
    textGradient,
    textBackground = [],
}) => {
    const glow = textGlow[0];
    const shadow = textShadow[0];
    const strokes = textStroke;
    const background = textBackground[0];

    const BACKGROUND_Z_INDEX = 10;
    const GLOW_Z_INDEX = BACKGROUND_Z_INDEX + 1;
    const SHADOW_Z_INDEX = GLOW_Z_INDEX + 1;
    const STROKE_MAX_Z_INDEX = SHADOW_Z_INDEX + (strokes?.length || 0);
    const TOP_Z_INDEX = STROKE_MAX_Z_INDEX + 1;

    const hasNoGradient = !textGradient || textGradient.colors.length < 2;
    const hasNoValidEffect =
        (!glow || glow.hidden) &&
        (!shadow || shadow.hidden) &&
        (strokes.length === 0 || strokes.every((stroke) => stroke.hidden)) &&
        (!background || background.hidden);

    const colorStyle = (() => {
        // 没用特效或渐变则使用默认颜色
        if (usage === 'entry' && hasNoGradient && hasNoValidEffect) {
            return { color: DEFAULT_COLOR };
        }
        return getColorStyle(color, textGradient);
    })();

    return (
        <div className="text-effect-display">
            {background && !background.hidden ? (
                <div
                    className="text-effect-display__layer"
                    data-role="background"
                    style={{ zIndex: BACKGROUND_Z_INDEX }}
                >
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: background.color,
                            borderRadius: background.roundWeight,
                            color: 'transparent',
                        }}
                    />
                </div>
            ) : null}

            {glow && !glow.hidden ? (
                <div className="text-effect-display__layer" data-role="glow" style={{ zIndex: GLOW_Z_INDEX }}>
                    <span
                        style={{
                            ...getGlowStyle(
                                glow,
                                strokes.reduce((sum, stroke) => sum + stroke.width * 2, 0),
                            ),
                        }}
                    >
                        A
                    </span>
                </div>
            ) : null}

            {shadow && !shadow.hidden ? (
                <div className="text-effect-display__layer" data-role="shadow" style={{ zIndex: SHADOW_Z_INDEX }}>
                    <span style={{ ...getShadowStyle(shadow) }}>A</span>
                </div>
            ) : null}

            {strokes?.length > 0
                ? strokes.map((stroke, index) =>
                      stroke.hidden ? null : (
                          <div
                              className="text-effect-display__layer"
                              key={index}
                              data-role={`stroke-${index}`}
                              style={{ zIndex: STROKE_MAX_Z_INDEX - index }}
                          >
                              <span style={{ ...getStrokeStyle(strokes, stroke, index) }}>A</span>
                          </div>
                      ),
                  )
                : null}

            <div className="text-effect-display__layer" data-role="top" style={{ zIndex: TOP_Z_INDEX }}>
                <span style={{ ...colorStyle }}>A</span>
            </div>
        </div>
    );
};
